import clsx from 'clsx';
import ConditionalComponent from '@/components/common/ConditionalComponent';
import ConditionalRender from '@/components/common/ConditionalRender';
import event from '@/utilites/gTag';
import Image from 'next/image';
import Link from 'next/link';
import LoadingComponent from '@/components/common/LoadingComponent';
import SalesChart from '../SalesChart/salesChart';
import SkuDescription from '@/components/skuDescription';
import SkuIsAskOwnerDesktop from '@/components/sku/Desktop/SkuIsAskOwnerDesktop';
import SkuIsBidOwnerDesktop from '@/components/sku/Desktop/SkuIsBidOwnerDesktop';
import SkuMarketData from '../Common/SkuMarketData';
import SkuPriceHistory from '../Common/SkuPriceHistory';
import SkuTopActions from '../SkuTopActions';
import SpiritoryBottleSliderBanner from '../SpiritoryBottle/SpiritoryBottleSliderBanner';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SimilarProductsDesktop } from '@/components/SimilarProducts';
import { SkuEnum } from '@/types/translations/SkuEnum';
import { SkuPageProps } from '@/types/props/components/skus';
import { toDe } from '@/utilites/sku-operations';
import { useRouter } from 'next/router';
import { isDefaultSkuDate } from '@/utilites/sku/sku.dates.utils';
import { ImagesSkuPriceTagSvg, ImagesSkuGreenCheckSvg } from '@/assets/images_sku.assets';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import SkuFeatures from '../Common/SkuFeatures';
import SkuSellNow from '../Common/SkuSellNow';
import SkuQuestions from '../Common/SkuQuestions';
import Sku12MonthHistorical from '../Common/Sku12MonthHistorical';
import SkuProductDetails from '../Common/SkuProductDetails';
import { SkuMainImage } from '../../../utilites/get-sku-main-image';
import useEvents from '@/hooks/useEvents';

export default function SkuPageDesktop({
  skuMainImageUrl,
  skuBrandName,
  skuSlug,
  skuName,
  userAuthenticated,
  tSkus,
  skuCategoryName,
  skuCountryOfOrigin,
  handleOpenPortfolioModal,
  skuBottleSizeInMl,
  skuVolumeInPercent,
  skuReleaseDate,
  skuBrandCountryOfOrigin,
  skuBrandRegion,
  skuDistilledYear,
  skuDescription,
  skuAsks,
  skuBids,
  skuOrders,
  skuRetailPrice,
  skuAverageSalePrice,
  skuCurrentMarketValue,
  skuTotalReturn,
  similarSkus,
  lastActivities,
  ownBidsAsks,
  memoizedFilteredAsk,
  highestBid,
  lastSale,
  bidsAskLoading,
  loadingMarketData,
  period,
  setPeriod,
  chartData,
  spiritoryBottle,
  isAdmin,
  lastActivitiesLoading,
  skuTotalReturnPercentage,
  userId,
  setShowShareModal,
  sku,
}: SkuPageProps) {
  const router = useRouter();
  const { translate } = useTypedTranslation<SkuEnum>('sku');
  const { fireBuyNowButtonClickEvent, firePlaceBidButtonClickEvent } = useEvents('', []);

  const handleClickBuy = (href: string) => {
    fireBuyNowButtonClickEvent({
      slug: skuSlug,
      product_name: skuName,
      product_category: skuCategoryName,
      product_price: memoizedFilteredAsk,
      page_type: 'product',
    });
    router.push(href);
  };

  const handleClickBid = (href: string) => {
    firePlaceBidButtonClickEvent({
      slug: skuSlug,
      product_name: skuName,
      product_category: skuCategoryName,
      product_price: memoizedFilteredAsk,
      page_type: 'product',
    });
    router.push(href);
  };

  return (
    <>
      <div className="tw-bg-customGray">
        <div className="tw-container tw-mx-auto tw-max-w-7xl tw-px-3">
          <div>
            <div>
              <SkuTopActions
                skuSlug={skuSlug}
                skuName={skuName}
                userAuthenticated={userAuthenticated}
                tSkus={tSkus}
                skuCategoryName={skuCategoryName}
                skuCountryOfOrigin={skuCountryOfOrigin}
                handleOpenPortfolioModal={handleOpenPortfolioModal}
                setShowShareModal={setShowShareModal}
                sku={sku}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="tw-container tw-mx-auto tw-max-w-7xl tw-px-3 tw-pt-8">
        <ConditionalRender condition={!spiritoryBottle}>
          <div className="tw-flex tw-items-start child:tw-basis-2/4">
            <div></div>
            <div>
              <SkuIsBidOwnerDesktop
                isBidOwner={ownBidsAsks.ownBids.isBidOwner}
                bidValue={ownBidsAsks.ownBids.bidValue}
              />
              <SkuIsAskOwnerDesktop
                isAskOwner={ownBidsAsks.ownAsks.isAskOwner}
                askValue={ownBidsAsks.ownAsks.askValue}
              />
            </div>
          </div>
        </ConditionalRender>
        <ConditionalRender condition={spiritoryBottle}>
          <div className="tw-flex tw-items-start tw-pb-2 child:tw-w-2/4">
            <div></div>
            <div>
              <SpiritoryBottleSliderBanner />
            </div>
          </div>
        </ConditionalRender>
        <div className="tw-flex tw-items-start child:tw-basis-2/4">
          <div className="tw-h-[588px] tw-pt-4">
            <SkuMainImage
              src={skuMainImageUrl}
              alt={skuName}
              width={1080}
              height={1080}
              className="tw-h-full tw-w-full tw-object-contain"
            />
          </div>
          <div>
            <p className="tw-mb-2 tw-font-plusJakartaSans tw-text-base tw-text-text">
              {skuBrandName}
            </p>
            <div className="tw-flex tw-flex-col tw-items-start">
              <h1 className="tw-font-plusJakartaSans tw-text-2xl tw-font-semibold tw-text-text">
                {skuName}
              </h1>
              <hr
                className="tw-m-0 tw-h-1 tw-w-2/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100"
                aria-hidden="true"
              />
              {isDefaultSkuDate(skuReleaseDate) === true || !skuReleaseDate ? (
                <p className="tw-my-3 tw-p-0 tw-text-slate-gray">
                  {translate(SkuEnum.condition)}:{' '}
                  <Link
                    href="/condition-guide"
                    className="tw-font-semibold tw-text-main tw-underline tw-underline-offset-4"
                  >
                    {translate(SkuEnum.like_new_ampersand_unopened)}
                  </Link>{' '}
                  |{' '}
                  <SkuDescription
                    skuBottleSizeInMl={skuBottleSizeInMl}
                    skuVolumeInPercent={skuVolumeInPercent}
                    skuReleaseDate={skuReleaseDate}
                  />{' '}
                </p>
              ) : (
                <p className="tw-my-3 tw-p-0 tw-text-slate-gray">
                  {translate(SkuEnum.condition)}{' '}
                  <Link
                    href="/condition-guide"
                    className="tw-font-semibold tw-text-main tw-underline tw-underline-offset-4"
                  >
                    {translate(SkuEnum.like_new_ampersand_unopened)}
                  </Link>{' '}
                  | {new Date(skuReleaseDate).getFullYear()}
                </p>
              )}
            </div>
            <LoadingComponent
              as="div"
              componentProps={{
                className: 'tw-contents',
              }}
              loading={bidsAskLoading}
            >
              <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-custom-gray tw-p-5">
                <div className="tw-flex tw-flex-col">
                  <div
                    className={clsx('tw-flex tw-items-start tw-justify-between', {
                      ['tw-hidden']: !memoizedFilteredAsk && bidsAskLoading === false,
                    })}
                  >
                    <div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <Image src={ImagesSkuPriceTagSvg} alt="Price Tag" width={20} height={20} />
                        <p className="tw-m-0 tw-p-0 tw-font-plusJakartaSans tw-text-base tw-font-medium tw-text-textBlack">
                          {translate(SkuEnum.buy_now_for)}
                        </p>
                      </div>
                      <div className="tw-pt-2">
                        <h2 className="tw-pt-3 tw-text-3xl tw-font-medium tw-text-text">
                          {toDe(memoizedFilteredAsk, {
                            withDigits: false,
                            withSymbol: true,
                          })}
                        </h2>
                      </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-medium tw-text-textBlack">
                      <p className="tw-m-0 tw-p-0">
                        {translate(SkuEnum.shipping_included_in_price)}{' '}
                      </p>
                      <Image
                        src={ImagesSkuGreenCheckSvg}
                        alt="Green Check"
                        width={16}
                        height={16}
                      />
                    </div>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-4 tw-pb-5 tw-pt-3 child:tw-basis-full">
                    <ConditionalComponent
                      condition={bidsAskLoading}
                      trueComponent={
                        <LoadingComponent
                          as={'div'}
                          loading={true}
                          componentProps={{
                            className: 'tw-flex tw-items-center tw-gap-4 child:tw-basis-full',
                          }}
                        >
                          <span className="tw-overflow-hidden tw-rounded-3xl tw-border tw-border-solid tw-border-transparent tw-px-6 tw-py-2.5 tw-font-semibold tw-shadow-md max-nine:tw-text-xs-custom nine:tw-max-w-xs">
                            {translate(SkuEnum.place_bid)}
                          </span>
                          <span className="tw-overflow-hidden tw-rounded-3xl tw-border tw-border-solid tw-border-transparent tw-px-6 tw-py-2.5 tw-font-semibold tw-shadow-md max-nine:tw-text-xs-custom nine:tw-max-w-xs">
                            {translate(SkuEnum.buy_now)}
                          </span>
                        </LoadingComponent>
                      }
                      falseComponent={
                        <>
                          <ConditionalRender
                            condition={(spiritoryBottle && isAdmin) || !spiritoryBottle}
                          >
                            <PrimaryButton
                              onClick={() => handleClickBid(clsx(`/${skuSlug}/buy`))}
                              outlined={true}
                              className={clsx(
                                memoizedFilteredAsk <= 0 &&
                                  'tw-w-full tw-max-w-full nine:tw-max-w-full'
                              )}
                            >
                              {translate(SkuEnum.place_bid)}
                            </PrimaryButton>
                          </ConditionalRender>
                          {spiritoryBottle ? (
                            memoizedFilteredAsk > 0 ? (
                              <PrimaryButton
                                onClick={() => handleClickBuy(clsx(`/${skuSlug}/buy?buy=true`))}
                                className="tw-w-full tw-max-w-full nine:tw-max-w-full"
                              >
                                {translate(SkuEnum.buy_now)}
                              </PrimaryButton>
                            ) : (
                              <PrimaryButton
                                className="tw-w-full tw-max-w-full nine:tw-max-w-full"
                                disabled={true}
                              >
                                {translate(SkuEnum.sold_out)}
                              </PrimaryButton>
                            )
                          ) : (
                            memoizedFilteredAsk > 0 && (
                              <PrimaryButton
                                onClick={() => handleClickBuy(clsx(`/${skuSlug}/buy?buy=true`))}
                                className="tw-w-full tw-max-w-full nine:tw-max-w-full"
                              >
                                {translate(SkuEnum.buy_now)}
                              </PrimaryButton>
                            )
                          )}
                        </>
                      }
                    />
                  </div>
                  <hr
                    className="tw-m-0 tw-h-0.5 tw-w-full tw-border tw-border-t-0 tw-border-none tw-border-custom-gray tw-bg-customCardGray tw-p-0 tw-opacity-100"
                    aria-hidden="true"
                  />
                  <LoadingComponent
                    as="div"
                    componentProps={{
                      className: 'tw-contents',
                    }}
                    loading={loadingMarketData}
                  >
                    <div className="tw-flex tw-items-center tw-justify-between tw-pt-5">
                      <h2 className="tw-m-0 tw-p-0 tw-font-plusJakartaSans tw-text-base tw-font-medium tw-text-textBlack">
                        {translate(SkuEnum.last_sale)}:{' '}
                        <span className="tw-font-normal">
                          {lastSale
                            ? toDe(lastSale, {
                                withDigits: false,
                                withSymbol: true,
                              })
                            : translate(SkuEnum.no_sales_yet)}
                        </span>
                      </h2>
                      <SkuMarketData
                        skuAsks={skuAsks}
                        skuBids={skuBids}
                        skuOrders={skuOrders}
                        loadingMarketData={loadingMarketData}
                      />
                    </div>
                  </LoadingComponent>
                </div>
              </div>
            </LoadingComponent>
            <SkuSellNow
              highestBid={highestBid}
              bidsAskLoading={bidsAskLoading}
              spiritoryBottle={spiritoryBottle}
              isAdmin={isAdmin}
              skuSlug={skuSlug}
              skuName={skuName}
              skuCategoryName={skuCategoryName}
            />

            <SkuFeatures />
          </div>
        </div>
      </div>
      <div className={clsx('tw-container tw-relative tw-mx-auto tw-max-w-7xl tw-px-3 tw-py-12')}>
        <div className="tw-relative tw-flex tw-items-start tw-justify-between tw-gap-12">
          <div className="tw-basis-2/6">
            <SkuProductDetails
              skuBottleSizeInMl={skuBottleSizeInMl}
              skuBrandCountryOfOrigin={skuBrandCountryOfOrigin}
              skuBrandName={skuBrandName}
              skuBrandRegion={skuBrandRegion}
              skuCategoryName={skuCategoryName}
              skuDistilledYear={skuDistilledYear}
              skuReleaseDate={skuReleaseDate}
            />
          </div>
          <div
            className={clsx('tw-basis-3/5', {
              ['tw-hidden']: !skuDescription,
            })}
          >
            <h2 className="pb-3 tw-relative tw-text-2xl tw-font-semibold tw-text-textBlack">
              {translate(SkuEnum.description)}
            </h2>
            <p className="tw-m-0 tw-p-0 tw-text-textBlack">
              {skuDescription || translate(SkuEnum.no_description_yet)}{' '}
            </p>
          </div>
        </div>
      </div>

      <div className="tw-container tw-relative tw-mx-auto tw-max-w-7xl tw-px-3 tw-pt-10">
        <div className="tw-relative tw-flex tw-h-[420px] tw-items-stretch tw-justify-between tw-gap-4">
          <div className="tw-flex tw-basis-3/5 tw-flex-col tw-justify-between">
            <h2 className="pb-3 tw-relative tw-text-2xl tw-font-semibold tw-text-textBlack">
              {translate(SkuEnum.price_history)}
            </h2>
            <LoadingComponent as="div" loading={lastActivitiesLoading}>
              <SalesChart chartData={chartData} period={period} setPeriod={setPeriod} />
            </LoadingComponent>
          </div>
          <Sku12MonthHistorical
            skuOrders={skuOrders}
            skuCurrentMarketValue={skuCurrentMarketValue}
            skuAverageSalePrice={skuAverageSalePrice}
            skuTotalReturn={skuTotalReturn}
            lastActivitiesLoading={lastActivitiesLoading}
            skuTotalReturnPercentage={skuTotalReturnPercentage}
          />
        </div>
      </div>

      <div className="tw-container tw-mx-auto tw-max-w-7xl tw-px-3 tw-pt-20">
        <div className="tw-mb-4 tw-w-fit">
          <h2 className="tw-text-2xl tw-font-semibold tw-text-text">
            {translate(SkuEnum.consider_these_similar_products)}
          </h2>
          <hr
            className="tw-m-0 tw-h-1 tw-w-2/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100"
            aria-hidden="true"
          />
        </div>
        <div>
          <SimilarProductsDesktop skus={similarSkus} userId={userId} />
        </div>
      </div>

      <SkuPriceHistory
        lastActivities={lastActivities}
        skuMainImageUrl={skuMainImageUrl}
        skuName={skuName}
        skuVolumeInPercent={skuVolumeInPercent}
        skuReleaseDate={skuReleaseDate}
        skuBottleSizeInMl={skuBottleSizeInMl}
        skuOrders={skuOrders}
        skuRetailPrice={skuRetailPrice}
        lastActivitiesLoading={lastActivitiesLoading}
      />

      <div className="tw-container tw-mx-auto tw-max-w-7xl tw-px-3 tw-py-20">
        <div className="tw-flex tw-flex-col tw-rounded-xl tw-bg-[#F5F5F5] tw-p-8">
          <div className="tw-flex tw-items-center tw-justify-between tw-pb-6">
            <h2 className="tw-text-3xl tw-font-semibold tw-text-text">
              {translate(SkuEnum.do_you_have_any_questions)}
            </h2>
            <a
              rel="noreferrer"
              href={'mailto:info@spiritory.com?subject=Spiritory%20Question'}
              title={translate(SkuEnum.contact_us)}
              target="_blank"
            >
              <PrimaryButton outlined={true} className="!tw-max-w-['unset'] !tw-px-16">
                {translate(SkuEnum.contact_us)}
              </PrimaryButton>
            </a>
          </div>
          <SkuQuestions />
        </div>
      </div>
    </>
  );
}
